import { fetchWithAuth } from './api';

const REST_URL = import.meta.env.VITE_REST_API_URL as string;

export interface ShippingAddress {
  name: string;
  line1: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface PriceBreakdown {
  printCostCents: number;
  shippingCostCents: number;
  luluFeeCents: number;
  totalCents: number;
}

export async function fetchQuote(params: {
  bindingType: 'softcover' | 'hardcover';
  shippingAddress: ShippingAddress;
  pageCount?: number;
}): Promise<PriceBreakdown> {
  const res = await fetchWithAuth(`${REST_URL}orders/quote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) throw new Error(`Quote failed: ${res.status}`);
  return res.json() as Promise<PriceBreakdown>;
}

export async function createPaymentIntent(params: {
  bindingType: 'softcover' | 'hardcover';
  shippingAddress: ShippingAddress;
  bookTitle: string;
  breakdown: PriceBreakdown;
  userEmail?: string;
}): Promise<{ clientSecret: string; orderId: string }> {
  const res = await fetchWithAuth(`${REST_URL}orders/intent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) throw new Error(`Intent creation failed: ${res.status}`);
  return res.json() as Promise<{ clientSecret: string; orderId: string }>;
}

export function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export interface PrintOrder {
  orderId: string;
  status: string;
  bindingType?: 'softcover' | 'hardcover';
  bookTitle?: string;
  breakdown?: PriceBreakdown;
  trackingUrl?: string;
  luluJobId?: number;
  createdAt: string;
  updatedAt: string;
}

export async function fetchOrders(): Promise<PrintOrder[]> {
  const res = await fetchWithAuth(`${REST_URL}orders`, { method: 'GET' });
  if (!res.ok) throw new Error(`GET /orders failed: ${res.status}`);
  const data = (await res.json()) as { orders: PrintOrder[] };
  return data.orders;
}

export interface PdfJobStatus {
  jobId: string;
  status: 'pending' | 'processing' | 'running' | 'complete' | 'failed' | 'error';
  stage?: string;
  downloadUrl?: string;
  pageCount?: number;
}

export type LatestPdfResult =
  | { exists: false }
  | { exists: true; downloadUrl: string; generatedAt?: string; pageCount?: number };

export async function triggerPdfExport(): Promise<{ jobId: string }> {
  const res = await fetchWithAuth(`${REST_URL}export/pdf`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!res.ok) throw new Error(`PDF export trigger failed: ${res.status}`);
  return res.json() as Promise<{ jobId: string }>;
}

export async function pollPdfStatus(jobId: string): Promise<PdfJobStatus> {
  const res = await fetchWithAuth(`${REST_URL}export/pdf/${jobId}`, { method: 'GET' });
  if (!res.ok) throw new Error(`PDF status poll failed: ${res.status}`);
  return res.json() as Promise<PdfJobStatus>;
}

export async function fetchLatestPdf(): Promise<LatestPdfResult> {
  const res = await fetchWithAuth(`${REST_URL}export/pdf/latest`, { method: 'GET' });
  if (!res.ok) throw new Error(`Latest PDF fetch failed: ${res.status}`);
  return res.json() as Promise<LatestPdfResult>;
}
